import React from 'react'
import Head from 'next/head'
import styled, { css } from 'styled-components'

import { white, gray_1, secondaryForSite } from 'components/corpSite/components/theme/colors'
import { spacing_3, spacing_2 } from 'components/corpSite/components/theme/spacing'
import { xsMd } from 'components/corpSite/components/theme/utils/media-query'

import Button from 'components/corpSite/components/shared/button'

import StaticLayout from 'components/corpSite/components/careguide-corp/pages/static-layout'
import Hero from 'components/corpSite/components/careguide-corp/colored-hero'
import Layout from 'components/corpSite/components/careguide-corp/layout'
import Paper from 'components/corpSite/components/careguide-corp/paper'
import FlexContainer from 'components/corpSite/components/shared/flex-container'
import Header from 'components/corpSite/components/careguide-corp/header'
import Subheader from 'components/corpSite/components/careguide-corp/subheader'
import ImageTile from 'components/corpSite/components/careguide-corp/image-tile'
import TrustTile from 'components/corpSite/components/careguide-corp/pages/home/trust'
import Stats from 'components/corpSite/components/careguide-corp/stats'
import Testimonials from 'components/corpSite/components/careguide-corp/pages/home/testimonials'
import Article from 'components/corpSite/components/careguide-corp/article'

import CaregiverImg from 'components/corpSite/imgs/careguide-corp/home-caregiver.png'
import PayrollImg from 'components/corpSite/imgs/careguide-corp/home-payroll.png'
import CcaImg from 'components/corpSite/imgs/articles/cca_blog.jpg'
import NannyShareImg from 'components/corpSite/imgs/articles/nanny-share-home.jpg'
import BetakitImg from 'components/corpSite/imgs/careguide-corp/betakit.png'
import HuffpostImg from 'components/corpSite/imgs/careguide-corp/huffpost.png'
import MashableImg from 'components/corpSite/imgs/careguide-corp/mashable.png'
import TechvibesImg from 'components/corpSite/imgs/careguide-corp/techvibes.png'
import ConnectImg from 'components/corpSite/imgs/careguide-corp/connect.jpg'
import HomePhone from 'components/corpSite/imgs/careguide-corp/home_iphoneX.png'

const stats = {
  customers: '3M+',
  messages: '12M+',
  calls: '645K+',
}

const articles = [
  {
    imagePath: NannyShareImg,
    header: 'What Is a Nanny Share? Everything You Need to Know',
    date: 'October 19, 2018',
    path: 'https://careguide.com/blog/careguide-nanny-share',
    subheader:
      "Here at CareGuide, we know people need help with a multitude of the people and places they cherish. \
      It's not often we find a way to care for it all,..",
  },
  {
    imagePath: CcaImg,
    header: 'Child Care Advantage is at Home with CareGuide',
    date: 'June 04, 2016',
    path: 'https://careguide.com/blog/child-care-advantage-is-at-home-with-careguide',
    subheader:
      'After much hard work and dedication, we are pleased to announce the official integration of \
      Child Care Advantage to the CareGuide family!..',
  },
]

const featuredBy = [BetakitImg, HuffpostImg, MashableImg, TechvibesImg]

const FeaturedContainer = styled(FlexContainer)`
  display: flex;
  justify-content: space-evenly;
  ${xsMd(css`
    width: 328px;
    flex-wrap: wrap;
    justify-content: center;
  `)}
`

const ConnectContainer = styled(Layout)`
  width: 100vw;
  text-align: center;

  background-image: url(${ConnectImg});
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: absolute;

  section {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    flex-direction: column;
    width: 704px;
  }
  h1 {
    color: ${white};
  }
  h3 {
    color: ${white};
  }
`

const ConnectButton = styled(Button)`
  width: 224px;
  align-self: center;
  margin-top: ${spacing_3};

  &:hover {
    background: transparent;
  }
`

function Home(props) {
  return (
    <>
      <Head>
        <title> The Best Caregivers for Child Care, Home Care, Pet Care, and Elder Care. </title>
        <meta
          content='We believe good people deserve the best care. Connect with housekeepers, nannies, pet sitters, and elder \
        care providers with ease. Trusted and secure. '
          name='description'
        />
        <meta
          content='Care services, Caregiver Services, Child care, Child care services, Caregiver agency, Home care agency, \
        Home care services, Home care'
          name='keywords'
        />
      </Head>
      <StaticLayout {...props}>
        <Hero
          header={"Everyone's Care, Everywhere"}
          subheader={
            'Finding care for what you value most has never been easier. We help connect the best with the \
          best.'
          }
          textColor={white}
          image={HomePhone}
        />
        <Layout>
          <Paper>
            <Header>All Kinds of Care</Header>
            <Subheader>
              <span>
                From nannies to housekeepers, our communities have no shortage of people who care.
              </span>
            </Subheader>
            <FlexContainer direction='row' flexWrap justifyContent='center' alignItems='center'>
              <ImageTile
                imagePath={CaregiverImg}
                href='/care-services'
                header='Care Services'
                subheader='We stand with caregivers by connecting them to dignified, safe, and legal employment.'
              />
              <ImageTile
                imagePath={PayrollImg}
                href='/payroll-services'
                header='Payroll Services'
                subheader='Your time is too valuable to be spent worrying about payroll. We take care of it all.'
              />
            </FlexContainer>
          </Paper>
        </Layout>
        <Layout bgColor='rgb(4,133,126)' padding={`${spacing_3} ${spacing_2}`}>
          <Paper>
            <TrustTile {...props} />
          </Paper>
        </Layout>
        <Layout bgColor={gray_1}>
          <Paper>
            <FlexContainer
              direction='column'
              flexWrap
              justifyContent='center'
              alignItems='center'
              maxWidth='694px'
            >
              <Header>Trust in Numbers</Header>
              <Subheader>
                Millions of people connect through CareGuide for their home, pet, and family care
                needs.
              </Subheader>
              <Stats stats={stats} />
            </FlexContainer>
          </Paper>
        </Layout>
        <Layout>
          <FlexContainer direction='column' flexWrap justifyContent='center' alignItems='center'>
            <Header marginBottom={spacing_3} textAlign='center'>
              What Our Customers Say
            </Header>
            <Testimonials />
          </FlexContainer>
        </Layout>
        <Layout bgColor={gray_1}>
          <Paper>
            <FlexContainer direction='column'>
              <Header marginBottom={spacing_3} marginBottomMobile={spacing_3}>
                See Our Latest News & Ideas
              </Header>
              {articles && articles.length > 0 && articles.map((article, index) => (
                <Article
                  key={index}
                  imagePath={article.imagePath}
                  header={article.header}
                  path={article.path}
                  subheader={article.subheader}
                  date={article.date}
                />
              ))}
            </FlexContainer>
          </Paper>
        </Layout>
        <Layout>
          <Paper>
            <Header>Proudly Featured By</Header>
            <FeaturedContainer>
              {featuredBy && featuredBy.length > 0 && featuredBy.map((logo, index) => (
                <img key={index} src={logo} width='135' height='100' />
              ))}
            </FeaturedContainer>
          </Paper>
        </Layout>
        <ConnectContainer>
          <section>
            <h1>Let’s Connect</h1>
            <h3>Whatever your care needs, we’re always here to help.</h3>
            <ConnectButton href={'/contact'} secondary emphasis disabled transparent>
              Contact Us
            </ConnectButton>
          </section>
        </ConnectContainer>
      </StaticLayout>
    </>
  )
}

export default Home
